import { useState, useEffect } from 'react';
import { adminAPI } from '../../lib/api';
import { ChevronLeft, ChevronRight, Loader2, BookOpen } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';

const ENTITY_COLORS = {
  lead: 'bg-blue-100 text-blue-800',
  company: 'bg-green-100 text-green-800',
  service: 'bg-amber-100 text-amber-800',
  landing_page: 'bg-purple-100 text-purple-800',
  blog_post: 'bg-pink-100 text-pink-800',
  settings: 'bg-slate-100 text-slate-800',
  content_blocks: 'bg-teal-100 text-teal-800',
  user: 'bg-orange-100 text-orange-800',
};

export default function AuditLogsPage() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [entityFilter, setEntityFilter] = useState('');

  useEffect(() => {
    setLoading(true);
    const params = { page, limit: 50 };
    if (entityFilter) params.entity = entityFilter;
    adminAPI.getAuditLogs(params).then(r => {
      setLogs(r.data.logs);
      setTotal(r.data.total);
      setPages(r.data.pages);
    }).finally(() => setLoading(false));
  }, [page, entityFilter]);

  return (
    <div data-testid="admin-audit-logs">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-semibold">Islem Kaydi (Audit Log)</h1>
        <Badge variant="secondary">{total} kayit</Badge>
      </div>

      <div className="mb-4">
        <select
          value={entityFilter}
          onChange={e => { setEntityFilter(e.target.value); setPage(1); }}
          className="flex h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
          data-testid="audit-entity-filter"
        >
          <option value="">Tum Varliklar</option>
          {Object.keys(ENTITY_COLORS).map(e => <option key={e} value={e}>{e}</option>)}
        </select>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-32"><Loader2 className="h-6 w-6 animate-spin" /></div>
          ) : logs.length === 0 ? (
            <p className="text-sm text-muted-foreground py-12 text-center">Kayit bulunamadi</p>
          ) : (
            <div className="divide-y">
              {logs.map(log => (
                <div key={log.id} className="p-4 flex items-start gap-3 hover:bg-slate-50">
                  <BookOpen className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium">{log.user_email}</span>
                      <Badge variant="secondary" className={`text-xs ${ENTITY_COLORS[log.entity] || ''}`}>{log.entity}</Badge>
                      <span className="text-sm text-muted-foreground">{log.action}</span>
                    </div>
                    {log.details && <p className="text-sm text-muted-foreground mt-0.5">{log.details}</p>}
                    <p className="text-xs text-muted-foreground mt-1 tabular-nums">{new Date(log.created_at).toLocaleString('tr-TR')}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {pages > 1 && (
        <div className="flex items-center justify-between mt-4">
          <p className="text-sm text-muted-foreground">Sayfa {page} / {pages}</p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}><ChevronLeft className="h-4 w-4" /></Button>
            <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => setPage(p => p + 1)}><ChevronRight className="h-4 w-4" /></Button>
          </div>
        </div>
      )}
    </div>
  );
}
